import type React from "react";
import { theme, emptyFrame, type as text, pageHeading } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { fmtDate } from "../lib/format";

// Renders one assurance pack snapshot — the same component on the organisation's preview and on
// the recipient's page (pages/AssuranceShare.tsx), so what the organisation checks before
// publishing is exactly what the recipient reads. Mirrors PackSnapshot in
// backend/src/services/assurancePack.ts. Tokens only, no fixed heights, so it prints cleanly.

export type ControlAnswer = "yes" | "partial" | "no" | "not_evidenced";

export interface InsurerQuestion {
  key: string;
  question: string;
  answer: ControlAnswer;
  evidence: string;
  /** Present when the organisation added its own statement. */
  statement?: { answer: Exclude<ControlAnswer, "not_evidenced">; text: string };
}

export type SnapshotSectionBody =
  | { kind: "facts"; rows: Array<{ label: string; value: string }> }
  | { kind: "questions"; questions: InsurerQuestion[] }
  | { kind: "table"; columns: string[]; rows: string[][]; note?: string };

export interface PackSnapshot {
  version: 1;
  generatedAt: string;
  organisation: { name: string; sector: string | null; size: string | null; countries: string[] };
  title: string;
  audience: string;
  recipientLabel: string;
  introduction: string | null;
  sections: Array<{ id: string; label: string; body: SnapshotSectionBody }>;
}

export const ANSWER_LABELS: Record<ControlAnswer, string> = { yes: "Yes", partial: "Partly", no: "No", not_evidenced: "Not evidenced" };

const ANSWER_TONE: Record<ControlAnswer, StatusTone> = { yes: "met", partial: "partial", no: "gap", not_evidenced: "none" };

export function AnswerPill({ answer }: { answer: ControlAnswer }) {
  return <StatusTag tone={ANSWER_TONE[answer] ?? "none"}>{ANSWER_LABELS[answer] ?? answer}</StatusTag>;
}

const sectionHeading: React.CSSProperties = {
  ...text.sectionTitle, margin: "0 0 10px",
};

function Facts({ rows }: { rows: Array<{ label: string; value: string }> }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 240px) 1fr", rowGap: 8, columnGap: 16, fontSize: text.body.fontSize }}>
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} style={{ display: "contents" }}>
          <div style={{ color: theme.color.muted }}>{r.label}</div>
          <div style={{ color: theme.color.text, minWidth: 0, overflowWrap: "anywhere" }}>{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ columns, rows, note }: { columns: string[]; rows: string[][]; note?: string }) {
  return (
    <div>
      {rows.length > 0 && (
        <div style={{ overflowX: "auto", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
            <thead>
              <tr style={{ textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize }}>
                {columns.map((c) => <th key={c} style={{ padding: "9px 12px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${theme.color.border}`, breakInside: "avoid" }}>
                  {r.map((cell, j) => (
                    <td key={j} style={{ padding: "9px 12px", color: j === 0 ? theme.color.text : theme.color.muted, fontWeight: j === 0 ? 600 : 400, verticalAlign: "top" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {note && <div style={{ ...text.hint, marginTop: rows.length ? 8 : 0 }}>{note}</div>}
      {!rows.length && !note && <div style={{ ...text.hint }}>Nothing recorded.</div>}
    </div>
  );
}

function Questions({ questions }: { questions: InsurerQuestion[] }) {
  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}` }}>
      {questions.map((q) => (
        <div key={q.key} style={{ padding: "12px 0", borderBottom: `1px solid ${theme.color.border}`, breakInside: "avoid" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ ...text.body, fontWeight: 600, flex: "1 1 280px", minWidth: 0 }}>{q.question}</div>
            <AnswerPill answer={q.answer} />
          </div>
          <div style={{ ...text.hint, marginTop: 4, lineHeight: 1.5 }}>{q.evidence}</div>
          {q.statement && (
            <div style={{ marginTop: 8, padding: "8px 12px", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, background: theme.color.panelAlt, fontSize: text.hint.fontSize, color: theme.color.text, lineHeight: 1.5 }}>
              <span style={{ fontWeight: 600 }}>The organisation's statement: {ANSWER_LABELS[q.statement.answer] ?? q.statement.answer}</span>
              {" — "}{q.statement.text}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function AssuranceSnapshotView({ snapshot }: { snapshot: PackSnapshot }) {
  const org = snapshot.organisation;
  const meta = [org.sector, org.size, org.countries.length ? org.countries.join(", ") : null].filter(Boolean).join(" · ");
  return (
    <div style={{ color: theme.color.text, display: "grid", gap: 22 }}>
      <div>
        <div style={text.eyebrow}>Security assurance pack</div>
        <div style={{ ...pageHeading, margin: "4px 0 0" }}>{org.name}</div>
        <div style={{ ...text.itemTitle, marginTop: 2 }}>{snapshot.title}</div>
        {meta && <div style={{ ...text.hint, marginTop: 4 }}>{meta}</div>}
        <div style={{ ...text.hint, marginTop: 2 }}>Prepared for {snapshot.recipientLabel}</div>
      </div>

      {snapshot.introduction && (
        <div style={{ ...text.body, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{snapshot.introduction}</div>
      )}

      {snapshot.sections.map((s) => (
        <section key={s.id}>
          <h2 style={sectionHeading}>{s.label}</h2>
          {s.body.kind === "facts" && <Facts rows={s.body.rows} />}
          {s.body.kind === "table" && <Table columns={s.body.columns} rows={s.body.rows} note={s.body.note} />}
          {s.body.kind === "questions" && <Questions questions={s.body.questions} />}
        </section>
      ))}

      {snapshot.sections.length === 0 && <div style={{ ...emptyFrame, ...text.body, color: theme.color.subtle }}>No sections chosen.</div>}

      <div style={{ ...text.meta, borderTop: `1px solid ${theme.color.border}`, paddingTop: 10 }}>
        Generated {fmtDate(snapshot.generatedAt)}. Figures are the organisation's own records at that date.
      </div>
    </div>
  );
}
